import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/auth';

/**
 * Two-step, email-verified registration.
 *   Step 1 — collect details → POST /api/register/start (backend emails a code).
 *   Step 2 — enter the 6-digit code → POST /api/register/verify → logged in.
 *
 * The code expires in 15 minutes; a live countdown and a rate-limited resend
 * keep the flow honest without the user leaving the page.
 */
const CODE_TTL_SECONDS = 15 * 60; // mirrors REGISTER_CODE_TTL_MINUTES (backend)
const RESEND_COOLDOWN = 60;       // mirrors REGISTER_RESEND_INTERVAL_SECONDS

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const Register = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', confirmPassword: '', company_name: '',
  });
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const [ttl, setTtl] = useState(0);          // seconds until the code expires
  const [cooldown, setCooldown] = useState(0); // seconds until resend allowed
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (authService.isLoggedIn()) navigate('/dashboard');
  }, [navigate]);

  // Single 1s ticker drives both the expiry countdown and resend cooldown.
  useEffect(() => {
    if (step !== 2) return undefined;
    const id = setInterval(() => {
      setTtl((t) => (t > 0 ? t - 1 : 0));
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    if (step === 2 && codeInputRef.current) codeInputRef.current.focus();
  }, [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // --- Step 1: submit details, trigger the verification email ---
  const handleStart = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    // Company is mandatory — every account gets its own isolated workspace.
    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }

    setLoading(true);
    try {
      await authService.registerStart({
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        company_name: formData.company_name.trim(),
      });
      setStep(2);
      setTtl(CODE_TTL_SECONDS);
      setCooldown(RESEND_COOLDOWN);
      setNotice(`We sent a 6-digit code to ${formData.email.trim()}.`);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2: verify the emailed code ---
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      await authService.registerVerify(formData.email.trim(), code);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError('');
    setNotice('');
    setLoading(true);
    try {
      await authService.registerResend(formData.email.trim());
      setTtl(CODE_TTL_SECONDS);
      setCooldown(RESEND_COOLDOWN);
      setCode('');
      // The backend responds generically (it may throttle silently), so we
      // can't promise delivery — keep the copy honest.
      setNotice('If your registration is still active, a new code is on its way.');
    } catch (err) {
      setError(err.message || 'Could not resend the code.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'block w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 placeholder-gray-400 ' +
    'text-white shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm';

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-md">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-6" aria-hidden="true">
        <span className={`h-2 w-8 rounded-full ${step >= 1 ? 'bg-blue-500' : 'bg-gray-600'}`} />
        <span className={`h-2 w-8 rounded-full ${step >= 2 ? 'bg-blue-500' : 'bg-gray-600'}`} />
      </div>

      {error && (
        <div className="rounded-md bg-red-900 p-4 mb-5" role="alert" aria-live="assertive">
          <p className="text-sm text-red-100">{error}</p>
        </div>
      )}
      {notice && !error && (
        <div className="rounded-md bg-blue-900/50 border border-blue-700 p-4 mb-5" aria-live="polite">
          <p className="text-sm text-blue-100">{notice}</p>
        </div>
      )}

      {step === 1 ? (
        <>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white">Create your account</h2>
            <p className="mt-2 text-sm text-gray-400">Free forever · verify your email to get started</p>
          </div>

          <form className="space-y-4" onSubmit={handleStart}>
            <div>
              <label htmlFor="username" className="sr-only">Username</label>
              <input id="username" name="username" type="text" required autoComplete="username"
                autoCapitalize="none" spellCheck={false} className={inputClass}
                placeholder="Username" value={formData.username} onChange={handleChange} />
            </div>
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <input id="email" name="email" type="email" required autoComplete="email"
                className={inputClass} placeholder="Email address"
                value={formData.email} onChange={handleChange} />
            </div>
            <div>
              <label htmlFor="company_name" className="sr-only">Company name</label>
              <input id="company_name" name="company_name" type="text" required
                autoComplete="organization" className={inputClass} placeholder="Company name"
                value={formData.company_name} onChange={handleChange} />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input id="password" name="password" type="password" required autoComplete="new-password"
                className={inputClass} placeholder="Password (min. 8 characters)"
                value={formData.password} onChange={handleChange} />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="sr-only">Confirm password</label>
              <input id="confirmPassword" name="confirmPassword" type="password" required
                autoComplete="new-password" className={inputClass} placeholder="Confirm password"
                value={formData.confirmPassword} onChange={handleChange} />
            </div>

            <button type="submit" disabled={loading}
              className="w-full rounded-md bg-blue-600 py-2.5 px-4 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60">
              {loading ? 'Sending code…' : 'Send verification code'}
            </button>
          </form>
        </>
      ) : (
        <>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white">Enter your code</h2>
            <p className="mt-2 text-sm text-gray-400">
              We emailed a 6-digit code to <span className="text-gray-200">{formData.email.trim()}</span>
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleVerify}>
            <div>
              <label htmlFor="code" className="sr-only">6-digit code</label>
              <input
                id="code" name="code" ref={codeInputRef}
                inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                className="block w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-3 text-center text-2xl tracking-[0.5em] font-semibold text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <p className="mt-2 text-center text-xs text-gray-500">
                {ttl > 0 ? <>Code expires in <span className="text-gray-300">{fmt(ttl)}</span></>
                         : <span className="text-danger">Code expired — request a new one.</span>}
              </p>
            </div>

            <button type="submit" disabled={loading || ttl === 0}
              className="w-full rounded-md bg-blue-600 py-2.5 px-4 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60">
              {loading ? 'Verifying…' : 'Verify & create account'}
            </button>
          </form>

          <div className="mt-5 flex items-center justify-between text-sm">
            <button type="button" onClick={() => { setStep(1); setError(''); setNotice(''); setCode(''); }}
              className="text-gray-400 hover:text-gray-200">
              ← Edit details
            </button>
            <button type="button" onClick={handleResend} disabled={cooldown > 0 || loading}
              className="font-medium text-blue-400 hover:text-blue-300 disabled:text-gray-500 disabled:cursor-not-allowed">
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </button>
          </div>
        </>
      )}

      <div className="mt-6 text-center text-sm">
        <p className="text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
